import React, { useState, useEffect } from 'react';
import { 
  Cone, Fence, ShieldCheck, Cctv, Mail, Globe, MapPin, Phone, Download, Calendar,
  FileText, ExternalLink, ChevronLeft, ChevronRight, ChevronDown, ChevronUp,
  Facebook, Instagram, Linkedin, Youtube, Share2, Bookmark, CheckCircle2, Shield,
  Award, Star, BadgeCheck, Building2, HardHat, Plane, Factory, Users, Zap, Target,
  Clock, FileCheck, AlertTriangle, UserCheck, GraduationCap, Wrench
} from 'lucide-react';

const BusinessCard = () => {
  const [expandedDivision, setExpandedDivision] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => { setTimeout(() => setIsLoaded(true), 100); }, []);

  // NEW LOGO with better visibility
  const companyLogo = "https://customer-assets.emergentagent.com/job_gov-contractor-brief/artifacts/uv39zt47_1000544513.png";
  const karlDentPhoto = "https://s3.amazonaws.com/app.elify.com/vbc3/59387/1617578483842-977637162.jpeg";

  // Company Video
  const companyVideo = "https://customer-assets.emergentagent.com/job_5531aa8d-4ed4-4291-9c3e-4ca0f3894dde/artifacts/u1r909z7_videoplayback.mp4";

  // TRUST CREDENTIALS - Company level only (no duplication)
  const companyCredentials = [
    { label: 'SDVOSB Certified', icon: Shield },
    { label: 'BBB A+ Rated', icon: Award },
    { label: 'CO Springs Licensed', icon: BadgeCheck },
    { label: 'Denver Licensed', icon: BadgeCheck },
  ];

  // EXECUTIVE OVERSIGHT CERTIFICATIONS - Leadership only
  const executiveCerts = [
    'Colorado POST Certified',
    'Dept. of Army Military Police',
    'Dept. of Army Civilian Police',
    'Dept. of Air Force Civilian Police',
    'FEMA Emergency Management',
    'AHA First Aid Certified',
    'Forklift Operator Certified'
  ];

  // OPERATIONAL FIELD CERTIFICATIONS - Separate section
  const operationalCerts = [
    { label: 'Flagger Certified', icon: CheckCircle2 },
    { label: 'Traffic Control Technician', icon: Wrench },
    { label: 'Traffic Control Supervisor', icon: UserCheck },
    { label: 'Traffic Design Specialist', icon: FileCheck },
    { label: 'Guardrail Installation', icon: Wrench },
    { label: 'Stop Sign Installation', icon: Wrench },
    { label: 'TMA Operator', icon: CheckCircle2 },
    { label: 'ATSSA Certified', icon: BadgeCheck },
    { label: 'OSHA 30 Certified', icon: Shield },
  ];

  // NAICS Codes
  const naicsCodes = [
    { code: '561612', desc: 'Security Guards & Patrol Services' },
    { code: '561612-10', desc: 'Traffic Control Services' },
    { code: '561990', desc: 'All Other Support Services' },
  ];

  // Four Divisions
  const divisions = [
    {
      icon: Cone, label: 'Traffic Control Operations', color: 'bg-orange-500',
      tagline: 'CDOT and MUTCD-aligned roadway safety operations.',
      services: ['Engineered Traffic Control Plans', 'ATSSA Certified Supervisors', 'Lane Closures & Detours', 'TMA Operations', 'Guardrail Installation', 'Temporary Traffic Devices', 'Work-Zone Monitoring', '24/7 Emergency Deployment']
    },
    {
      icon: Fence, label: 'Barricade & Temporary Fencing', color: 'bg-slate-500',
      tagline: 'Structured site control for safety and compliance.',
      services: ['Temporary Fencing Installation', 'Work-Zone Barricade Systems', 'Pedestrian Channelization', 'Perimeter Control', 'Rapid Setup & Adjustments']
    },
    {
      icon: ShieldCheck, label: 'Security & Asset Protection', color: 'bg-blue-500',
      tagline: 'Licensed operations with disciplined public safety standards.',
      services: ['Access Control & Checkpoints', 'Construction Site Security', 'Critical Infrastructure Protection', 'Mobile Patrol & Sweeps', 'Incident Documentation', 'Facility Security', 'Crowd Management']
    },
    {
      icon: Cctv, label: 'Mobile Surveillance & Monitoring', color: 'bg-emerald-500',
      tagline: 'Reducing theft, vandalism, liability, and disruption.',
      services: ['Trailer-Mounted Camera Units', 'Live Remote Monitoring', 'Real-Time Alerts', 'Short & Long-Term Deployment', 'Construction Theft Prevention']
    }
  ];

  // Why RMPS
  const whyRmpsHighlights = [
    { icon: Target, label: 'Unified Safety Operations' },
    { icon: Zap, label: 'Reduced Vendor Gaps' },
    { icon: AlertTriangle, label: 'Coordinated Incident Response' },
    { icon: FileCheck, label: 'Infrastructure-Ready Compliance' },
    { icon: Clock, label: 'Rapid Multi-Division Deployment' },
    { icon: Award, label: 'Licensed & BBB-Rated' },
  ];

  // Industries
  const industries = [
    { icon: HardHat, label: 'Highway & Municipal Construction' },
    { icon: Building2, label: 'Infrastructure & Utility Projects' },
    { icon: Plane, label: 'Airports & Government Facilities' },
    { icon: Factory, label: 'Commercial & Industrial Sites' },
    { icon: Shield, label: 'Critical Infrastructure' },
    { icon: Users, label: 'Large Public Events' },
  ];

  const socialLinks = [
    { icon: Facebook, label: 'Facebook', url: '#' },
    { icon: () => <span className="font-bold text-sm">𝕏</span>, label: 'X', url: '#' },
    { icon: Instagram, label: 'Instagram', url: '#' },
    { icon: Linkedin, label: 'LinkedIn', url: '#' },
    { icon: Youtube, label: 'YouTube', url: '#' }
  ];

  const toggleDivision = (index) => setExpandedDivision(expandedDivision === index ? null : index);

  const handleScheduleConsultation = () => window.open('https://www.rmpsllc.com/', '_blank');
  const handleRequestQuote = () => window.open('https://www.rmpsllc.com/contact', '_blank');
  
  // Native vCard download - no third-party dependency
  const handleSaveContact = () => {
    const link = document.createElement('a');
    link.href = '/karl-dent.vcf';
    link.download = 'karl-dent.vcf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  
  const handleForwardMyInfo = () => {
    if (navigator.share) {
      navigator.share({
        title: 'Dr. Karl O\'Brian Dent - RMPSINC',
        text: 'Contact information for Dr. Karl O\'Brian Dent, President of Rocky Mountain Protective Service Inc.',
        url: window.location.href
      }).catch(() => {});
    } else {
      // Fallback: copy URL to clipboard
      navigator.clipboard.writeText(window.location.href);
      alert('Link copied to clipboard!');
    }
  };

  return (
    <div className={`max-w-md mx-auto bg-slate-900 min-h-screen transition-opacity duration-500 ${isLoaded ? 'opacity-100' : 'opacity-0'}`}>
      
      {/* Gold Top Accent */}
      <div className="h-1 bg-gradient-to-r from-amber-600 via-amber-400 to-amber-600"></div>

      {/* ==================== HERO SECTION - Tighter spacing ==================== */}
      <section className="px-4 pt-4 pb-6">
        {/* Logo - 25% larger with better contrast */}
        <div className="flex items-center gap-4">
          <div className="relative flex-shrink-0">
            <img 
              src={companyLogo} 
              alt="RMPSINC Logo" 
              className="w-28 h-28 object-contain drop-shadow-2xl"
            />
          </div>
          <div className="flex-1">
            <h1 className="font-oswald text-white text-lg font-bold tracking-tight uppercase leading-tight">
              Rocky Mountain
            </h1>
            <h2 className="font-oswald text-amber-400 text-lg font-bold tracking-tight uppercase">
              Protective Service Inc.
            </h2>
            <p className="font-mono text-slate-500 text-[9px] tracking-[0.15em] mt-0.5">GOVERNMENT CONTRACTOR</p>
          </div>
        </div>

        {/* Services Grid - Tighter */}
        <div className="mt-4 grid grid-cols-2 gap-1.5">
          {['Infrastructure Safety', 'Traffic Control', 'Security Operations', 'Mobile Surveillance'].map((s) => (
            <span key={s} className="text-[9px] font-mono text-slate-300 uppercase tracking-wider px-2 py-1 bg-slate-800/80 border border-slate-700 text-center">
              {s}
            </span>
          ))}
        </div>

        {/* Tagline - Tighter spacing */}
        <div className="mt-4">
          <p className="font-inter text-slate-300 text-sm leading-snug">Four Integrated Divisions. One Mission:</p>
          <p className="font-oswald text-amber-400 text-lg font-semibold">Total Site Protection.</p>
        </div>

        <p className="mt-2 font-inter text-slate-400 text-[11px] leading-relaxed">
          Certified, rapid-deployment safety and protection solutions for construction, infrastructure, municipal, federal, and commercial projects across Colorado.
        </p>

        {/* 24/7 Badge */}
        <div className="mt-3 inline-flex items-center gap-2 bg-emerald-900/30 border border-emerald-600/40 px-3 py-1.5">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
          </span>
          <span className="font-mono text-[9px] text-emerald-400 tracking-wider uppercase">24/7 Emergency Deployment</span>
        </div>
      </section>

      {/* ==================== COMPANY TRUST CREDENTIALS ==================== */}
      <section className="px-4 py-4 bg-slate-800/40">
        <div className="flex flex-wrap justify-center gap-1.5">
          {companyCredentials.map((c, i) => (
            <div key={i} className="flex items-center gap-1.5 bg-amber-500/10 border border-amber-500/30 text-amber-400 text-[9px] font-mono px-2 py-1.5">
              <c.icon className="w-3 h-3" />
              {c.label}
            </div>
          ))}
        </div>
        <div className="flex justify-center gap-6 mt-3">
          <div className="text-center">
            <p className="font-mono text-[8px] text-slate-500 uppercase">CAGE</p>
            <p className="font-mono text-xs text-white font-semibold">78HS3</p>
          </div>
          <div className="text-center">
            <p className="font-mono text-[8px] text-slate-500 uppercase">UEI</p>
            <p className="font-mono text-xs text-white font-semibold">MME1B5MLBE69</p>
          </div>
        </div>
      </section>

      {/* ==================== NAICS CAPABILITY CODES ==================== */}
      <section className="px-4 py-4">
        <p className="font-mono text-[9px] text-amber-500 tracking-[0.2em] uppercase text-center mb-2">NAICS Capability Codes</p>
        <div className="grid grid-cols-1 gap-1.5">
          {naicsCodes.map((n, i) => (
            <div key={i} className="flex items-center justify-between bg-slate-800 border border-slate-700 px-3 py-2">
              <span className="font-mono text-amber-400 text-[11px] font-semibold">{n.code}</span>
              <span className="text-slate-300 text-[10px]">{n.desc}</span>
            </div>
          ))}
        </div>
      </section>

      {/* ==================== FOUR DIVISIONS ==================== */}
      <section className="px-4 py-5 bg-slate-800/40">
        <p className="font-mono text-[9px] text-amber-500 tracking-[0.2em] uppercase text-center">Capabilities</p>
        <h3 className="font-oswald text-white text-sm font-bold uppercase text-center mt-1 mb-3">Four Integrated Divisions</h3>
        
        <div className="space-y-1.5">
          {divisions.map((div, index) => (
            <div key={index} className="bg-slate-800 border border-slate-700 overflow-hidden">
              <button onClick={() => toggleDivision(index)} className="w-full flex items-center justify-between p-2.5 hover:bg-slate-700/50 transition-colors">
                <div className="flex items-center gap-2.5">
                  <div className={`w-9 h-9 ${div.color} rounded flex items-center justify-center`}>
                    <div.icon className="w-4 h-4 text-white" />
                  </div>
                  <span className="font-oswald text-white text-xs font-semibold">{div.label}</span>
                </div>
                {expandedDivision === index ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
              </button>
              {expandedDivision === index && (
                <div className="px-3 pb-3 border-t border-slate-700">
                  <div className="grid grid-cols-2 gap-1 mt-2">
                    {div.services.map((s, i) => (
                      <div key={i} className="flex items-start gap-1">
                        <CheckCircle2 className="w-2.5 h-2.5 text-amber-500 flex-shrink-0 mt-0.5" />
                        <span className="text-slate-300 text-[9px] leading-tight">{s}</span>
                      </div>
                    ))}
                  </div>
                  <p className="mt-2 text-[9px] text-amber-400/80 italic">{div.tagline}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* ==================== OPERATIONAL FIELD CERTIFICATIONS ==================== */}
      <section className="px-4 py-5">
        <p className="font-mono text-[9px] text-amber-500 tracking-[0.2em] uppercase text-center">Field Qualifications</p>
        <h3 className="font-oswald text-white text-sm font-bold uppercase text-center mt-1 mb-3">Operational Certifications</h3>
        
        <div className="grid grid-cols-3 gap-1.5">
          {operationalCerts.map((cert, i) => (
            <div key={i} className="bg-slate-800 border border-slate-700 p-2 text-center">
              <cert.icon className="w-4 h-4 text-amber-500 mx-auto mb-1" />
              <span className="text-slate-300 text-[8px] leading-tight block">{cert.label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* ==================== WHY RMPS ==================== */}
      <section className="px-4 py-5 bg-slate-800/40">
        <p className="font-mono text-[9px] text-amber-500 tracking-[0.2em] uppercase text-center">The RMPS Advantage</p>
        <h3 className="font-oswald text-white text-sm font-bold uppercase text-center mt-1 mb-2">Why Choose RMPS</h3>
        
        <p className="text-slate-400 text-[10px] text-center mb-3 leading-relaxed">
          Integrated traffic control, barricades, surveillance, and security under certified executive oversight.
        </p>

        <div className="grid grid-cols-2 gap-1.5 mb-3">
          {whyRmpsHighlights.map((h, i) => (
            <div key={i} className="flex items-center gap-2 bg-slate-800 border border-slate-700 p-2">
              <h.icon className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" />
              <span className="text-white text-[9px]">{h.label}</span>
            </div>
          ))}
        </div>

        <div className="bg-amber-500/10 border border-amber-500/30 p-2 text-center">
          <p className="font-oswald text-amber-400 text-[10px] font-semibold tracking-wider uppercase">
            One Partner. Full Accountability. Total Operational Control.
          </p>
        </div>
      </section>

      {/* ==================== EXECUTIVE LEADERSHIP ==================== */}
      <section className="px-4 py-5">
        <p className="font-mono text-[9px] text-amber-500 tracking-[0.2em] uppercase text-center">Executive Leadership</p>
        <h3 className="font-oswald text-white text-sm font-bold uppercase text-center mt-1 mb-3">Certified Oversight</h3>

        <div className="bg-slate-800 border border-slate-700 p-3 border-l-4 border-l-amber-500">
          <div className="flex items-start gap-3">
            <div className="w-16 h-16 overflow-hidden border border-amber-500/40 flex-shrink-0">
              <img src={karlDentPhoto} alt="Dr. Karl O'Brian Dent" className="w-full h-full object-cover object-top" />
            </div>
            <div>
              <h4 className="font-oswald text-white text-sm font-bold">Dr. Karl O'Brian Dent</h4>
              <p className="font-oswald text-amber-400 text-[10px] tracking-wider">PRESIDENT</p>
              <p className="text-slate-500 text-[9px] mt-0.5">Rocky Mountain Protective Service Inc.</p>
            </div>
          </div>

          {/* Executive Oversight Certifications ONLY */}
          <div className="mt-3 pt-3 border-t border-slate-700">
            <p className="font-mono text-[8px] text-slate-500 uppercase mb-2">Executive Oversight Certifications</p>
            <div className="grid grid-cols-2 gap-1">
              {executiveCerts.map((cert, i) => (
                <div key={i} className="flex items-center gap-1">
                  <GraduationCap className="w-2.5 h-2.5 text-amber-500 flex-shrink-0" />
                  <span className="text-slate-300 text-[8px]">{cert}</span>
                </div>
              ))}
            </div>
          </div>

          <p className="mt-3 text-[9px] text-slate-500 italic border-t border-slate-700 pt-2">
            Leadership grounded in public safety discipline, regulatory compliance, and mission-ready execution.
          </p>
        </div>
      </section>

      {/* ==================== INDUSTRIES SERVED ==================== */}
      <section className="px-4 py-5 bg-slate-800/40">
        <p className="font-mono text-[9px] text-amber-500 tracking-[0.2em] uppercase text-center">Markets</p>
        <h3 className="font-oswald text-white text-sm font-bold uppercase text-center mt-1 mb-3">Industries Served</h3>
        
        <div className="grid grid-cols-2 gap-1.5">
          {industries.map((ind, i) => (
            <div key={i} className="flex items-center gap-2 bg-slate-800 border border-slate-700 p-2">
              <ind.icon className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" />
              <span className="text-white text-[9px] leading-tight">{ind.label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* ==================== FINAL CTA - Reordered ==================== */}
      <section className="px-4 py-5">
        {/* Company Video */}
        <div className="relative overflow-hidden aspect-video mb-4 bg-black">
          <video 
            src={companyVideo}
            controls
            playsInline
            preload="metadata"
            className="w-full h-full object-cover"
            poster=""
          >
            Your browser does not support the video tag.
          </video>
        </div>

        {/* Headline */}
        <h3 className="font-oswald text-white text-base font-bold text-center mb-3">Ready to Secure Your Project?</h3>
        
        {/* Buttons - Tighter spacing */}
        <div className="space-y-2">
          <button onClick={handleScheduleConsultation} className="w-full h-11 bg-gradient-to-r from-amber-500 to-amber-600 text-slate-900 font-oswald text-xs font-bold tracking-wider uppercase flex items-center justify-center gap-2">
            <Calendar className="w-4 h-4" />
            Schedule Consultation
          </button>
          <button onClick={handleRequestQuote} className="w-full h-11 border-2 border-amber-500 text-amber-400 font-oswald text-xs font-bold tracking-wider uppercase flex items-center justify-center gap-2 hover:bg-amber-500 hover:text-slate-900 transition-all">
            <FileText className="w-4 h-4" />
            Request Pricing
          </button>
          <button className="w-full h-10 bg-slate-800 border border-slate-700 text-slate-300 font-oswald text-[10px] tracking-wider uppercase flex items-center justify-center gap-2 hover:bg-slate-700 transition-all">
            <Download className="w-3.5 h-3.5" />
            Download Capability Statement
          </button>
        </div>

        {/* Emergency Line */}
        <div className="mt-3 text-center">
          <span className="inline-flex items-center gap-1.5 text-emerald-400 text-[9px] font-mono uppercase tracking-wider">
            <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse"></span>
            24/7 Emergency Deployment Available
          </span>
        </div>

        {/* Contact Card - Tighter */}
        <div className="mt-4 bg-slate-800 border border-slate-700 p-3 text-center">
          <p className="font-oswald text-white text-sm font-bold">Dr. Karl O'Brian Dent</p>
          <p className="font-oswald text-amber-400 text-[10px] tracking-wider">PRESIDENT</p>
          <p className="text-slate-400 text-[9px] mt-1">102 S Tejon St Suite 1100, Colorado Springs, CO 80903</p>
          <a href="mailto:karl.dent@rmpsllcgov.com" className="text-amber-400 text-[9px] hover:underline">karl.dent@rmpsllcgov.com</a>
          <span className="text-slate-600 mx-2">|</span>
          <a href="https://www.rmpsllc.com" target="_blank" rel="noopener noreferrer" className="text-white text-[9px] hover:text-amber-400">www.rmpsllc.com</a>
        </div>

        {/* QR Code Section */}
        <div className="mt-4 bg-slate-800/50 border border-slate-700 p-4 text-center">
          <div className="inline-block bg-white p-3 rounded-lg">
            <img 
              src="https://customer-assets.emergentagent.com/job_5531aa8d-4ed4-4291-9c3e-4ca0f3894dde/artifacts/cwarr1sq_RMPS%20Karl%20Digital%20Card%20%281%29.svg" 
              alt="QR Code - Scan to open digital card"
              className="w-32 h-32 sm:w-36 sm:h-36 mx-auto"
            />
          </div>
          <p className="mt-3 font-mono text-[10px] text-slate-400 tracking-wider uppercase">Scan to open digital card</p>
        </div>
      </section>

      {/* ==================== QUICK ACTIONS ==================== */}
      <section className="px-4 py-4 bg-slate-800/40">
        <div className="flex justify-center gap-4">
          <a href="tel:3473486604" className="w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-lg">
            <Phone className="w-5 h-5 text-blue-500" />
          </a>
          <a href="mailto:karl.dent@rmpsllcgov.com" className="w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-lg">
            <Mail className="w-5 h-5 text-amber-500" />
          </a>
          <a href="https://maps.google.com/?q=102+S+Tejon+St+Suite+1100,+Colorado+Springs,+CO+80903" target="_blank" rel="noopener noreferrer" className="w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-lg">
            <MapPin className="w-5 h-5 text-red-500" />
          </a>
        </div>
      </section>

      {/* ==================== SAVE & FORWARD ==================== */}
      <section className="px-4 py-3">
        <div className="flex gap-2">
          <button onClick={handleForwardMyInfo} className="flex-1 h-10 bg-amber-500 text-slate-900 font-oswald text-[10px] font-bold tracking-wider uppercase flex items-center justify-center gap-1.5">
            <Share2 className="w-3.5 h-3.5" />
            Forward Info
          </button>
          <button onClick={handleSaveContact} className="flex-1 h-10 bg-slate-700 text-white font-oswald text-[10px] font-bold tracking-wider uppercase flex items-center justify-center gap-1.5">
            <Bookmark className="w-3.5 h-3.5" />
            Save Contact
          </button>
        </div>
        <div className="flex justify-center gap-1.5 mt-3">
          {socialLinks.map((s) => (
            <a key={s.label} href={s.url} className="w-8 h-8 bg-slate-800 border border-slate-700 rounded flex items-center justify-center text-slate-400 hover:text-white hover:border-slate-600 transition-all">
              <s.icon className="w-3.5 h-3.5" />
            </a>
          ))}
        </div>
      </section>

      {/* ==================== FOOTER ==================== */}
      <footer className="px-4 py-4 text-center bg-slate-900">
        <p className="font-mono text-[9px] text-slate-500 tracking-wider uppercase">One Partner. Full Accountability.</p>
        <p className="font-oswald text-amber-400 text-[10px] font-semibold">Total Operational Control.</p>
        <div className="mt-3 flex justify-center items-center gap-2">
          <span className="w-6 h-px bg-slate-700"></span>
          <span className="w-1.5 h-1.5 rotate-45 border border-amber-500/50"></span>
          <span className="w-6 h-px bg-slate-700"></span>
        </div>
        <p className="mt-3 text-[8px] text-slate-600">© 2026 Rocky Mountain Protective Service Inc.</p>
      </footer>
    </div>
  );
};

export default BusinessCard;
